import { useState, useEffect } from 'react';
import { NumberInput, ToggleGroup } from '@parsina/framework';
import { useTranslation } from '../../i18n';
import { C } from '../../constants/theme';
import { STACK } from '../../constants/density.js';
import { DURATION_OPTIONS, DURATION_MIN, DURATION_MAX } from '../../screens/Booking/constants';

/**
 * The range a SERVICE session may take. Wider than a visit's (5–120): a
 * physiotherapy or a procedure session can run for hours, and the backend only
 * asks that it be positive.
 */
export const SERVICE_DURATION_MIN = 5;
export const SERVICE_DURATION_MAX = 480;

/** A service session length the save may send: a whole number within the range. */
export const isServiceDuration = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= SERVICE_DURATION_MIN && n <= SERVICE_DURATION_MAX;
};

/**
 * A length in minutes: the four lengths a clinic actually uses as chips, plus
 * «دلخواه» for anything else.
 *
 * Born as People's «مدت هر ویزیت» (VisitDurationField, now a wrapper around this)
 * and shared with «خدمات و تعرفه»'s «مدت هر جلسه», where a plain number field made
 * the common case a typing exercise. The presets are the booking module's own
 * `DURATION_OPTIONS`, so the chips and the slot grid can never drift apart.
 *
 * A stored value that is not one of the presets opens ALREADY on «دلخواه» with
 * the field revealed — otherwise an existing 19 would render as nothing selected
 * and read as unset.
 *
 * `value` is the form's string ('' = unset); `onChange` receives a string.
 */
export default function DurationField({
  value, onChange, disabled, resetKey,
  label, hint, outOfRangeText,
  min = DURATION_MIN, max = DURATION_MAX,
  testid,
}) {
  const { t, formatNumber } = useTranslation();
  const raw = value == null ? '' : value;
  const numeric = raw === '' ? null : Number(raw);
  const isPreset = numeric != null && DURATION_OPTIONS.includes(numeric);

  // Sticky, not derived. Deriving "is this custom?" from the current value made
  // the field vanish the moment it was cleared — the value became null, null is
  // not a non-preset, and the input unmounted mid-edit. Once «دلخواه» is chosen
  // it stays chosen until a preset is clicked or a different record is opened.
  const [customMode, setCustomMode] = useState(false);
  useEffect(() => { setCustomMode(false); }, [resetKey]);

  const showCustom = customMode || (numeric != null && !isPreset);
  const outOfRange = numeric != null
    && (!Number.isInteger(numeric) || numeric < min || numeric > max);
  const rangeArgs = { min: formatNumber(min), max: formatNumber(max) };

  return (
    <div data-testid={testid} style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
      <ToggleGroup
        label={label}
        value={showCustom ? 'custom' : (isPreset ? String(numeric) : '')}
        disabled={disabled}
        onChange={(v) => {
          if (!v) return;                       // Radix emits '' when deselecting
          if (v === 'custom') { setCustomMode(true); return; }
          setCustomMode(false);
          onChange(v);
        }}
        options={[
          ...DURATION_OPTIONS.map((m) => ({ value: String(m), label: formatNumber(m) })),
          { value: 'custom', label: t('people.duration.custom') },
        ]}
      />
      {showCustom && (
        <NumberInput
          label={t('people.duration.customLabel')}
          helperText={t('people.duration.range', rangeArgs)}
          error={outOfRange ? (outOfRangeText ? outOfRangeText(rangeArgs) : t('people.duration.outOfRange', rangeArgs)) : ''}
          value={raw}
          disabled={disabled}
          min={min}
          max={max}
          onChange={(e) => onChange(String(e?.target ? e.target.value : e))}
        />
      )}
      {hint ? (
        <div style={{ fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.8 }}>{hint}</div>
      ) : null}
    </div>
  );
}
